import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { SocketContext } from "../../../../../contexts/SocketContext";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import IUiSocketSetup from "../../../../../models/IUiSocketSetup";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import Typeahead from "../../../../shared/Typeahead/Typeahead";
import ICatalog_Item from "../../../../../models/ICatalog_Item";
import ITypeaheadCatalogItemDTO from "../../../../../models/DTO/ITypeaheadCatalogItemDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IOnTypeaheadCatalogItemDTO from "../../../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import OwButton from "../../../../shared/OwButton/OwButton";
import IProcessRatioItemDTO from "../../../../../models/DTO/IProcessRatioItemDTO";
import INotification from "../../../../../models/INotification";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import UiContext from "../../../../../contexts/UiContext";
import ICategory from "../../../../../models/ICategory";
import { OwIconButton } from "../../../../shared/OwIconButton/OwIconButton";
import ICategory_Ratio_Item_Map from "../../../../../models/ICategory_Ratio_Item_Map";
import UIService from "../../../../../services/UIService";

type Props = {
    catalogGuid: string;
    category?: ICategory;
    refreshCategory: () => void;
};

export const RatioItemsSelection = (props: Props) => {
    const socket = useContext(SocketContext);
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiModel = useContext(UiContext);

    const [foundItems, setFoundItems] = useState<ICatalog_Item[]>([]);
    const [ratioFrom, setRatioFrom] = useState<number>();
    const [ratioTo, setRatioTo] = useState<number>();
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | null>(null);
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);

    const lookupItem = (name: string) => {
        const payload: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: props.catalogGuid,
            name,
            options: {
                searchName: true,
                searchDescription: true,
                searchSku: true,
                searchUpc: true,
            },
        };

        socket?.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
    };

    const onTypeheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundItems(data.catalog_items);
    };

    const addItem = () => {
        if (selectedItem && ratioFrom && ratioTo) {
            const payload: IProcessRatioItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: props.catalogGuid,
                categoryGuid: props.category!.guid,
                catalogItemGuid: selectedItem.guid,
                ratioFrom,
                ratioTo,
            };

            socket?.emit(EndpointsEnum.CatalogsProcessRatioItem, payload);
        }
    };

    const onProcessRatioItem = () => {
        setRatioFrom(0);
        setRatioTo(0);
        setSelectedItem(null);
        setFoundItems([]);
        setClearTypeahead(true);

        const notification: INotification = {
            message: "successfully_processed_ratio_item",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiModel.notifyFn(null, notification);

        props.refreshCategory();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeheadCatalogItem },
        { endpoint: EndpointsEnum.CatalogsOnProcessRatioItem, fn: onProcessRatioItem },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket?.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket?.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const selectedItemFn = (item: ICatalog_Item) => {
        setSelectedItem(item);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "ratio_from":
                setRatioFrom(Number(value));
                break;

            case "ratio_to":
                setRatioTo(Number(value));
                break;
        }
    };

    const removeRatioItem = (catalogItem: ICatalog_Item, item: ICategory_Ratio_Item_Map) => {
        console.log("removeRatioItem: ", item);
        const payload: IProcessRatioItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: props.catalogGuid,
            categoryGuid: props.category!.guid,
            catalogItemGuid: catalogItem.guid,
            ratioFrom: item.ratio_from,
            ratioTo: item.ratio_to,
            deleteFlag: true,
            ratioItemId: item.id,
        };

        socket?.emit(EndpointsEnum.CatalogsProcessRatioItem, payload);
    };

    let tableClass = "table table-dark";

    if (UIService.isMainViewLight()) {
        tableClass = `table`;
    }

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
    };

    return (
        <div className={"RatioItemsSelection"}>
            <div>
                <OwLabel title={"ratio_item"} />
                <div>
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={lookupItem}
                        titleProp={"name"}
                        subTitleProp={"sku"}
                        typeaheadItems={foundItems}
                        selectedItemFn={selectedItemFn}
                        scrollableList={true}
                        clearTypeaheadCallback={clearTypeaheadFn}
                        clearTypeahead={clearTypeahead}
                    />
                </div>
                <div className={"mt-2"}>
                    <OwLabel title={"ratio"} />
                    <div className={"d-flex"}>
                        <div className={"me-2"}>
                            <OwFnInput fn={handleChange} name={"ratio_from"} placeholder={"from"} type={"number"} value={ratioFrom} />
                        </div>
                        <div>
                            <OwFnInput fn={handleChange} name={"ratio_to"} placeholder={"to"} type={"number"} value={ratioTo} />
                        </div>
                    </div>
                </div>
                <div className={"mt-2"}>
                    <OwButton title={"add"} fn={addItem} />
                </div>
            </div>

            <div className={"mt-2 mb-2"}>
                <table className={tableClass}>
                    <thead>
                        <tr>
                            <th></th>
                            <th>
                                <OwLabel title={"sku"} />
                            </th>
                            <th>
                                <OwLabel title={"from"} />
                            </th>
                            <th>
                                <OwLabel title={"to"} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.category?.Ratio_Items?.map((ratioItem, index) => {
                            return (
                                <tr key={index}>
                                    <td>
                                        <OwIconButton
                                            iconClass={"fas fa-trash-alt"}
                                            iconColorClass={"text-danger"}
                                            fn={() => {
                                                removeRatioItem(ratioItem, ratioItem.Category_Ratio_Item_Map!);
                                            }}
                                        />
                                    </td>
                                    <td>{ratioItem.sku}</td>
                                    <td>{ratioItem.Category_Ratio_Item_Map?.ratio_from}</td>
                                    <td>{ratioItem.Category_Ratio_Item_Map?.ratio_to}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
